// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Crew Manager
 * 
 * Manages ship crew members, roles, morale, injuries, and casualties
 * Handles recruitment, firing, and crew bonuses
 */
import { generatePersonality, generatePreferences } from '../../config/CrewPersonalityConfig.js';
import recreationManager from './RecreationManager.js';
import { assignBackstory } from '../../data/crewBackstories.js';
import EventBus, { EVENT_TYPES } from '../system/EventBus.js';
import STORAGE_KEYS from '../system/STORAGE_KEYS';
import AlienSpeciesConfig from '../../config/AlienSpeciesConfig.js';

// Crew role definitions
export const CREW_ROLES = {
  PILOT: {
    id: 'pilot',
    name: 'Pilot',
    description: 'Expert navigator',
    bonus: 'Fuel Efficiency -10%',
    hireCost: 800,
    icon: '✈️'
  },
  ENGINEER: {
    id: 'engineer',
    name: 'Engineer',
    description: 'Repair specialist',
    bonus: 'Repair Speed +20%',
    hireCost: 700,
    icon: '🔧'
  },
  GUNNER: {
    id: 'gunner',
    name: 'Gunner',
    description: 'Weapons expert',
    bonus: 'Weapon Damage +15%',
    hireCost: 900,
    icon: '💥'
  },
  MEDIC: {
    id: 'medic',
    name: 'Medic',
    description: 'Medical officer',
    bonus: 'Casualties -50%',
    hireCost: 600,
    icon: '🏥'
  },
  SCIENTIST: {
    id: 'scientist',
    name: 'Scientist',
    description: 'Research specialist',
    bonus: 'Scan Quality +25%',
    hireCost: 700,
    icon: '🔬'
  },
  GENERIC: {
    id: 'generic',
    name: 'Crew Member',
    description: 'General crew',
    bonus: 'None',
    hireCost: 300,
    icon: '👤'
  }
};

// Experience levels
export const EXPERIENCE_LEVELS = {
  ROOKIE: { name: 'Rookie', multiplier: 0.8, stars: 1 },
  TRAINED: { name: 'Trained', multiplier: 1.0, stars: 2 },
  EXPERIENCED: { name: 'Experienced', multiplier: 1.2, stars: 3 },
  VETERAN: { name: 'Veteran', multiplier: 1.4, stars: 4 },
  ELITE: { name: 'Elite', multiplier: 1.6, stars: 5 }
};

// First names pool

// XP thresholds for leveling up
export const XP_THRESHOLDS = {
  ROOKIE: 0,
  TRAINED: 100,
  EXPERIENCED: 250,
  VETERAN: 500,
  ELITE: 1000
};

// XP rewards
export const XP_REWARDS = {
  COMBAT_KILL: 10,
  COMBAT_VICTORY: 25,
  MISSION_COMPLETE: 25,
  EXPLORATION: 15,
  TRADE: 10,
  TRAINING: 50
};
const FIRST_NAMES = [
  'Sarah', 'Marcus', 'Elena', 'James', 'Maya', 'David', 'Zara', 'Alex',
  'Chen', 'Yuki', 'Omar', 'Priya', 'Lars', 'Keiko', 'Ivan', 'Amara',
  'Diego', 'Nora', 'Kai', 'Zoe', 'Ravi', 'Luna', 'Erik', 'Mira'
];

// Last names pool
const LAST_NAMES = [
  'Chen', 'Webb', 'Rodriguez', 'Nakamura', 'Singh', 'O\'Brien', 'Kowalski', 'Santos',
  'Kim', 'Müller', 'Patel', 'Anderson', 'Yamamoto', 'Garcia', 'Ivanov', 'Okafor',
  'Martinez', 'Sato', 'Kumar', 'Jensen', 'Tanaka', 'Silva', 'Petrov', 'Hassan'
];

// Get hireable alien species (friendly, non-hostile)
const getHireableSpecies = () => {
  return Object.values(AlienSpeciesConfig).filter(species => 
    !species.hostile && 
    species.tradeable !== false &&
    species.traits?.temperament !== 'Hostile' &&
    species.traits?.temperament !== 'Aggressive'
  );
};

// Get random hireable species
const getRandomSpecies = () => {
  const hireableSpecies = getHireableSpecies();
  if (hireableSpecies.length === 0) {
    return null; // Fallback to human
  }
  return hireableSpecies[Math.floor(Math.random() * hireableSpecies.length)];
};

class CrewManager {
  constructor() {
    // Crew roster
    this.crew = [];
    
    // Crew capacity
    this.minCrew = 3;
    this.maxCrew = 15;  // Can be upgraded
    this.optimalCrew = 10;
    
    // Morale (0-100)
    this.morale = 75;
    
    // Injured crew count
    this.injuredCrew = 0;
    
    // Crew statistics
    this.stats = {
      totalHired: 0,
      totalFired: 0,
      totalKilled: 0,
      combatKills: 0,
      missionsCompleted: 0
    };
    
    // Load from localStorage
    this.loadFromStorage();
    
    // Migrate existing crew to add species if missing
    this.migrateCrewSpecies();
    
    // Initialize with starting crew if empty
    if (this.crew.length === 0) {
      this.initializeStartingCrew();
    }
  }

  /**
   * Initialize starting crew
   */
  initializeStartingCrew() {
    // Start with 10 crew members
    this.hireCrew(CREW_ROLES.PILOT, 'ROOKIE', true);
    this.hireCrew(CREW_ROLES.ENGINEER, 'ROOKIE', true);
    this.hireCrew(CREW_ROLES.GUNNER, 'ROOKIE', true);
    this.hireCrew(CREW_ROLES.MEDIC, 'ROOKIE', true);
    
    // Fill rest with generic crew
    for (let i = 0; i < 6; i++) {
      this.hireCrew(CREW_ROLES.GENERIC, 'ROOKIE', true);
    }
    
  }

  /**
   * Migrate existing crew to add species if missing
   */
  migrateCrewSpecies() {
    let migrated = 0;
    this.crew.forEach(member => {
      if (!member.species || !member.speciesData) {
        const species = getRandomSpecies();
        member.species = species?.id || 'human';
        member.speciesData = species || {
          id: 'human',
          name: 'Human',
          origin: 'Earth',
          appearance: { ascii: '👤', color: '#88ccff' },
          traits: { temperament: 'Neutral', intelligence: 'Average' }
        };
        migrated++;
      }
    });
    if (migrated > 0) {
      this.saveToStorage();
    }
  }

  /**
   * Generate random crew member
   */
  generateCrewMember(role, experienceLevel = 'TRAINED', speciesId = null) {
    const firstName = FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)];
    const lastName = LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)];
    const experience = EXPERIENCE_LEVELS[experienceLevel];
    
    // Get species (random if not specified)
    const species = speciesId ? AlienSpeciesConfig[speciesId] : getRandomSpecies();
    const speciesData = species || {
      id: 'human',
      name: 'Human',
      origin: 'Earth',
      appearance: { ascii: '👤', color: '#88ccff' },
      traits: { temperament: 'Neutral', intelligence: 'Average' }
    };
    
    return {
      id: `crew_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: `${firstName} ${lastName}`,
      species: speciesData.id,
      speciesData: speciesData,
      role: role.id,
      roleData: role,
      experience: experienceLevel,
      experienceData: experience,
      hiredAt: Date.now(),
      combatKills: 0,
      missionsCompleted: 0,
      xp: 0,
      injured: false,
      personality: generatePersonality(),
      preferences: generatePreferences()
    };
  }

  /**
   * Hire crew member
   */
  hireCrew(role, experienceLevel = 'TRAINED', isFree = false) {
    if (this.crew.length >= this.maxCrew) {
      return {
        success: false,
        message: 'Crew quarters at maximum capacity'
      };
    }

    const crewMember = this.generateCrewMember(role, experienceLevel);
    const cost = isFree ? 0 : role.hireCost;
    
    this.crew.push(crewMember);
    this.stats.totalHired++;
    
    // Check for personality conflicts with existing crew
    if (typeof window !== 'undefined' && window.CrewRelationshipManager) {
      const conflicts = [];
      this.crew.forEach(existingCrew => {
        if (existingCrew.id !== crewMember.id) {
          const result = window.CrewRelationshipManager.checkPersonalityConflict(crewMember, existingCrew);
          if (result.conflict) {
            conflicts.push(`${existingCrew.name} (${result.types.join(', ')})`);
          }
        }
      });
      
      if (conflicts.length > 0) {
      }
    }
    
    this.saveToStorage();
    EventBus.emit(EVENT_TYPES.CREW_UPDATED, { crew: this.getAllCrew(), morale: this.morale, injuredCrew: this.injuredCrew });

    return {
      success: true,
      crewMember,
      cost,
      message: `Hired ${crewMember.name} as ${role.name}`
    };
  }

  /**
   * Fire crew member
   */
  fireCrew(crewId) {
    const index = this.crew.findIndex(c => c.id === crewId);
    if (index === -1) {
      return {
        success: false,
        message: 'Crew member not found'
      };
    }

    if (this.crew.length <= this.minCrew) {
      return {
        success: false,
        message: `Cannot fire crew - minimum ${this.minCrew} required to operate ship`
      };
    }

    const crewMember = this.crew[index];
    this.crew.splice(index, 1);
    this.stats.totalFired++;
    this.saveToStorage();

    // Small morale hit
    this.adjustMorale(-2);
    EventBus.emit(EVENT_TYPES.CREW_UPDATED, { crew: this.getAllCrew(), morale: this.morale, injuredCrew: this.injuredCrew });

    return {
      success: true,
      refund: Math.floor(crewMember.roleData.hireCost * 0.5),
      message: `Fired ${crewMember.name}`
    };
  }

  /**
   * Kill crew member (casualties)
   */
  killCrew(count = 1, reason = 'combat') {
    const actualCount = Math.min(count, this.crew.length);
    const casualties = [];
    
    for (let i = 0; i < actualCount; i++) {
      if (this.crew.length > 0) {
        // Random crew member dies
        const index = Math.floor(Math.random() * this.crew.length);
        const crewMember = this.crew[index];
        casualties.push(crewMember);
        this.crew.splice(index, 1);
        this.stats.totalKilled++;
      }
    }
    
    // Morale hit for casualties
    this.adjustMorale(-5 * actualCount);
    
    this.saveToStorage();
    
    return {
      count: actualCount,
      casualties,
      reason,
      message: `Lost ${actualCount} crew member${actualCount > 1 ? 's' : ''} to ${reason}`
    };
  }

  /**
   * Injure crew members
   */
  injureCrew(count = 1) {
    const actualCount = Math.min(count, this.crew.length - this.injuredCrew);
    this.injuredCrew += actualCount;
    this.saveToStorage();
    EventBus.emit(EVENT_TYPES.CREW_UPDATED, { crew: this.getAllCrew(), morale: this.morale, injuredCrew: this.injuredCrew });

    return {
      count: actualCount,
      message: `${actualCount} crew member${actualCount > 1 ? 's' : ''} injured`
    };
  }

  /**
   * Heal injured crew
   */
  healCrew(count = 1) {
    const actualCount = Math.min(count, this.injuredCrew);
    this.injuredCrew -= actualCount;

    // Morale boost for healing
    this.adjustMorale(2 * actualCount);

    this.saveToStorage();
    EventBus.emit(EVENT_TYPES.CREW_UPDATED, { crew: this.getAllCrew(), morale: this.morale, injuredCrew: this.injuredCrew });

    return {
      count: actualCount,
      message: `Healed ${actualCount} crew member${actualCount > 1 ? 's' : ''}`
    };
  }

  /**
   * Get crew count
   */
  getCrewCount() {
    return this.crew.length;
  }

  /**
   * Get crew by role
   */
  getCrewByRole(roleId) {
    return this.crew.filter(c => c.role === roleId);
  }

  /**
   * Get crew bonuses
   */
  getBonuses() {
    const bonuses = {
      fuelEfficiency: 1.0,    // Pilot bonus
      repairSpeed: 1.0,       // Engineer bonus
      weaponDamage: 1.0,      // Gunner bonus
      casualtyReduction: 1.0, // Medic bonus
      scanQuality: 1.0        // Scientist bonus
    };

    // Apply pilot bonuses
    const pilots = this.getCrewByRole('pilot');
    pilots.forEach(pilot => {
      bonuses.fuelEfficiency -= 0.10 * pilot.experienceData.multiplier;
    });

    // Apply engineer bonuses
    const engineers = this.getCrewByRole('engineer');
    engineers.forEach(engineer => {
      bonuses.repairSpeed += 0.20 * engineer.experienceData.multiplier;
    });

    // Apply gunner bonuses
    const gunners = this.getCrewByRole('gunner');
    gunners.forEach(gunner => {
      bonuses.weaponDamage += 0.15 * gunner.experienceData.multiplier;
    });

    // Apply medic bonuses
    const medics = this.getCrewByRole('medic');
    if (medics.length > 0) {
      bonuses.casualtyReduction = 0.5; // 50% reduction
    }

    // Apply scientist bonuses
    const scientists = this.getCrewByRole('scientist');
    scientists.forEach(scientist => {
      bonuses.scanQuality += 0.25 * scientist.experienceData.multiplier;
    });

    // Apply morale multiplier
    const moraleMultiplier = this.getMoraleMultiplier();
    bonuses.repairSpeed *= moraleMultiplier.repair;
    bonuses.weaponDamage *= moraleMultiplier.combat;

    return bonuses;
  }

  /**
   * Get morale level
   */
  getMorale() {
    const baseMorale = this.morale;
    const recreationBonus = recreationManager.getFacilityBonuses().moraleBonus;
    return Math.min(100, baseMorale + recreationBonus);
  }

  /**
   * Adjust morale
   */
  adjustMorale(amount) {
    this.morale = Math.max(0, Math.min(100, this.morale + amount));
    this.saveToStorage();
  }

  /**
   * Set morale (for cheats)
   */
  setMorale(value) {
    this.morale = Math.max(0, Math.min(100, value));
    this.saveToStorage();
    EventBus.emit(EVENT_TYPES.CREW_UPDATED, { crew: this.getAllCrew(), morale: this.morale, injuredCrew: this.injuredCrew });
  }

  /**
   * Get morale status
   */
  getMoraleStatus() {
    if (this.morale >= 80) return { level: 'EXCELLENT', color: '#00ff88', emoji: '😊' };
    if (this.morale >= 60) return { level: 'GOOD', color: '#00ffff', emoji: '😐' };
    if (this.morale >= 40) return { level: 'LOW', color: '#ffaa00', emoji: '😟' };
    if (this.morale >= 20) return { level: 'CRITICAL', color: '#ff4444', emoji: '😠' };
    return { level: 'MUTINY RISK', color: '#ff0000', emoji: '⚔️' };
  }

  /**
   * Get morale multiplier for performance
   */
  getMoraleMultiplier() {
    if (this.morale >= 80) return { combat: 1.2, repair: 1.3 };
    if (this.morale >= 60) return { combat: 1.0, repair: 1.0 };
    if (this.morale >= 40) return { combat: 0.9, repair: 0.8 };
    if (this.morale >= 20) return { combat: 0.7, repair: 0.6 };
    return { combat: 0.5, repair: 0.4 };
  }

  /**
   * Check for mutiny risk
   */
  checkMutiny() {
    if (this.morale < 20) {
      const mutinyChance = (20 - this.morale) / 100;
      return {
        risk: true,
        chance: mutinyChance,
        message: '⚔️ MUTINY RISK - Crew morale critically low!'
      };
    }
    return { risk: false, chance: 0 };
  }

  /**
   * Get crew efficiency based on count
   */
  getEfficiency() {
    if (this.crew.length < this.minCrew) return 0;
    if (this.crew.length < this.optimalCrew) {
      return this.crew.length / this.optimalCrew;
    }
    return 1.0;
  }

  /**
   * Get all crew
   */
  getAllCrew() {
    return [...this.crew];
  }

  /**
   * Get crew member by ID
   */
  getCrew(crewId) {
    return this.crew.find(c => c.id === crewId);
  }

  /**
   * Award XP to crew member(s)
   */
  awardExperience(crewId, amount) {
    const crew = crewId === "all" ? this.crew : [this.getCrewMember(crewId)];
    const leveledUp = [];

    crew.forEach(member => {
      if (!member || member.injured) return;

      member.xp = (member.xp || 0) + amount;

      const currentLevel = member.experience;
      const nextLevel = this.getNextLevel(currentLevel);
      
      if (nextLevel && member.xp >= XP_THRESHOLDS[nextLevel]) {
        member.experience = nextLevel;
        member.experienceData = EXPERIENCE_LEVELS[nextLevel];
        leveledUp.push({ name: member.name, level: nextLevel });
      }
    });

    this.saveToStorage();
    return leveledUp;
  }

  /**
   * Get next experience level
   */
  getNextLevel(currentLevel) {
    const levels = ["ROOKIE", "TRAINED", "EXPERIENCED", "VETERAN", "ELITE"];
    const currentIndex = levels.indexOf(currentLevel);
    return currentIndex < levels.length - 1 ? levels[currentIndex + 1] : null;
  }

  /** Get crew member by ID
   */
  /**
   * Assign backstories to crew members who don't have them
   */
  assignBackstoriesToCrew() {
    this.crew.forEach(crewMember => {
      if (!crewMember.backstory) {
        crewMember.backstory = assignBackstory(crewMember);
      }
    });
    this.saveToStorage();
  }

  getCrewMember(crewId) {
    return this.crew.find(c => c.id === crewId);
  }

  /**
   * Update crew stats (after mission/combat)
   */
  updateCrewStats(type, value = 1) {
      // eslint-disable-next-line default-case
    switch (type) {
      case 'mission':
        this.stats.missionsCompleted += value;
        // Random crew members gain experience
        this.crew.forEach(crewMember => {
          crewMember.missionsCompleted += value;
        });
        break;
      case 'combat':
        this.stats.combatKills += value;
        // Gunners gain experience
        this.getCrewByRole('gunner').forEach(gunner => {
          gunner.combatKills += value;
        });
        break;
    }
    this.saveToStorage();
  }

  /**
   * Get crew statistics
   */
  getStats() {
    return { ...this.stats };
  }

  /**
  /**
   * Update training progress (called by GameTimeManager)
   */
  updateTrainingProgress() {
    // Import here to avoid circular dependency
    const crewTrainingManager = require('./CrewTrainingManager.js').default;
    return crewTrainingManager.updateTrainingProgress();
  }

  /**
   * Reset to starting values
   */
  reset() {
    this.crew = [];
    this.morale = 75;
    this.injuredCrew = 0;
    this.stats = {
      totalHired: 0,
      totalFired: 0,
      totalKilled: 0,
      combatKills: 0,
      missionsCompleted: 0
    };
    this.initializeStartingCrew();
    this.saveToStorage();
  }

  /**
   * Save to localStorage
   */
  saveToStorage() {
    try {
      const data = {
        crew: this.crew,
        morale: this.morale,
        injuredCrew: this.injuredCrew,
        stats: this.stats,
        relationships: this.relationships || {}
      };
      localStorage.setItem(STORAGE_KEYS.CREW, JSON.stringify(data));
    } catch (error) {
      console.error('[CrewManager] Failed to save to localStorage:', error);
    }
  }

  /**
   * Load from localStorage
   */
  loadFromStorage() {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.CREW);
      if (data) {
        const parsed = JSON.parse(data);
        this.crew = parsed.crew || [];
        this.morale = parsed.morale || 75;
        this.injuredCrew = parsed.injuredCrew || 0;
        this.stats = parsed.stats || this.stats;
        this.relationships = parsed.relationships || {};
      }
    } catch (error) {
      console.error('[CrewManager] Failed to load from localStorage:', error);
    }
  }
}

// Singleton instance
const crewManager = new CrewManager();
export default crewManager;
